import { EnrichmentVisibility } from '@aibees/elephant/components/visibility-enrichment-filter-select/config';
import { FEATURE_FLAGS } from '@aibees/elephant/constants';
import {
  EnrichmentTypeEnum,
  FeatureFlags,
  SearchType,
} from '@aibees/elephant/types';
import { Enrichment } from '@aibees/shared/definition';
import { isFeatureEnabled } from '@aibees/shared/feature-flags';
import {
  enrichmentRequestParser,
  enrichmentRequestValueFormatter,
} from '../components/filter-tags/utils';
import {
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterOption,
  SearchFilterType,
} from '../types';
import { enrichmentDataRequestApiMapper } from '../utils';

const VISIBILITY_TABS = ['All', 'Mine', 'Team'];

// One option per visibility tab. Enrichment records don't carry a usable
// per-item visibility field, so each tab filters server-side via the
// `visibility` query param (empty = All, PRIVATE = Mine, INTERNAL = Team) —
// not a client filterFn.
const buildEnrichmentVisibilityOptions = (
  type: EnrichmentTypeEnum,
  queryKey: string
): SearchFilterOption[] => {
  const base = `elephant-management/um/workspaces/:wid/enrichments?page=0&size=25&type=${type}`;
  return [
    { visibility: EnrichmentVisibility.ALL, label: 'All' },
    { visibility: EnrichmentVisibility.PRIVATE, label: 'Mine' },
    { visibility: EnrichmentVisibility.INTERNAL, label: 'Team' },
  ].map(({ visibility, label }) => ({
    type: 'URL',
    value: visibility ? `${base}&visibility=${visibility}` : base,
    label,
    icon: null,
    index: undefined,
    queryKeys: [queryKey],
  }));
};

// Per-tab counts come from the response `metadata` (per-visibility totals,
// scoped by type); mirrors useAddEnrichmentVisibilityMetadata (All = Mine + Team).
const enrichmentVisibilityTabsCount = (
  metadata?: Record<string, number>
): number[] | undefined => {
  const mine = metadata?.[EnrichmentVisibility.PRIVATE];
  const team = metadata?.[EnrichmentVisibility.INTERNAL];
  if (mine == null && team == null) return undefined;
  return [(mine ?? 0) + (team ?? 0), mine ?? 0, team ?? 0];
};

export default function enrichmentRequestFilterConfig(
  searchType: SearchType
): SearchFilterBase {
  const isEnrichmentEnabled = isFeatureEnabled(
    FEATURE_FLAGS,
    FeatureFlags.ENRICHMENT
  );

  const companiesEnrichmentRequest: SearchFilter = {
    name: 'Company',
    type: SearchFilterType.NONE,
    index: 2,
    key: 'companies_enrichment_request',
    field: 'companies_enrichment_request',
    metadata: {
      tabs: {
        icon: 'business',
        iconColor: 'primary',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Company Enrichment Requests',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'companies_enrichment_request_include',
        key: 'companies_enrichment_request_include',
        icon: 'business',
        metadata: {
          delimiter: '::',
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Select Company Enrichment Requests',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: Enrichment[]) =>
            enrichmentDataRequestApiMapper(data),
          valueFormatter: enrichmentRequestValueFormatter,
          tags: { labelFormatter: enrichmentRequestParser },
          getTabsCount: enrichmentVisibilityTabsCount,
        },
        searchKey: 'search',
        config: { labelKey: 'name', valueKey: 'id' },
        tabs: VISIBILITY_TABS,
        options: buildEnrichmentVisibilityOptions(
          EnrichmentTypeEnum.COMPANY,
          'companies_enrichment_request_query'
        ),
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        field: 'companies_enrichment_request_exclude',
        key: 'companies_enrichment_request_exclude',
        icon: 'business',
        metadata: {
          delimiter: '::',
          color: 'error',
          icon: 'block',
          placeholder: 'Select Company Enrichment Requests',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: Enrichment[]) =>
            enrichmentDataRequestApiMapper(data),
          valueFormatter: enrichmentRequestValueFormatter,
          tags: { labelFormatter: enrichmentRequestParser },
          getTabsCount: enrichmentVisibilityTabsCount,
        },
        searchKey: 'search',
        config: { labelKey: 'name', valueKey: 'id' },
        tabs: VISIBILITY_TABS,
        options: buildEnrichmentVisibilityOptions(
          EnrichmentTypeEnum.COMPANY,
          'companies_enrichment_request_query'
        ),
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 2,
        layer: 4,
        field: 'companies_enrichment_request_all_of',
        key: 'companies_enrichment_request_all_of',
        icon: 'business',
        metadata: {
          delimiter: '::',
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Select Company Enrichment Requests',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: Enrichment[]) =>
            enrichmentDataRequestApiMapper(data),
          valueFormatter: enrichmentRequestValueFormatter,
          tags: { labelFormatter: enrichmentRequestParser },
          getTabsCount: enrichmentVisibilityTabsCount,
        },
        searchKey: 'search',
        config: { labelKey: 'name', valueKey: 'id' },
        tabs: VISIBILITY_TABS,
        options: buildEnrichmentVisibilityOptions(
          EnrichmentTypeEnum.COMPANY,
          'companies_enrichment_request_query'
        ),
      },
    ],
  };

  const peopleEnrichmentRequest: SearchFilter = {
    name: 'People',
    type: SearchFilterType.NONE,
    index: 3,
    key: 'people_enrichment_request',
    field: 'people_enrichment_request',
    metadata: {
      tabs: {
        icon: 'people',
        iconColor: 'success',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'People Enrichment Requests',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'people_enrichment_request_include',
        key: 'people_enrichment_request_include',
        icon: 'people',
        metadata: {
          delimiter: '::',
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Select People Enrichment Requests',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: Enrichment[]) =>
            enrichmentDataRequestApiMapper(data),
          valueFormatter: enrichmentRequestValueFormatter,
          tags: { labelFormatter: enrichmentRequestParser },
          getTabsCount: enrichmentVisibilityTabsCount,
        },
        searchKey: 'search',
        config: { labelKey: 'name', valueKey: 'id' },
        tabs: VISIBILITY_TABS,
        options: buildEnrichmentVisibilityOptions(
          EnrichmentTypeEnum.PERSON,
          'people_enrichment_request_query'
        ),
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        field: 'people_enrichment_request_exclude',
        key: 'people_enrichment_request_exclude',
        icon: 'people',
        metadata: {
          delimiter: '::',
          color: 'error',
          icon: 'block',
          placeholder: 'Select People Enrichment Requests',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: Enrichment[]) =>
            enrichmentDataRequestApiMapper(data),
          valueFormatter: enrichmentRequestValueFormatter,
          tags: { labelFormatter: enrichmentRequestParser },
          getTabsCount: enrichmentVisibilityTabsCount,
        },
        searchKey: 'search',
        config: { labelKey: 'name', valueKey: 'id' },
        tabs: VISIBILITY_TABS,
        options: buildEnrichmentVisibilityOptions(
          EnrichmentTypeEnum.PERSON,
          'people_enrichment_request_query'
        ),
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 2,
        layer: 4,
        field: 'people_enrichment_request_all_of',
        key: 'people_enrichment_request_all_of',
        icon: 'people',
        metadata: {
          delimiter: '::',
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Select People Enrichment Requests',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
          },
        },
        utils: {
          optionsApiFormatter: (data?: Enrichment[]) =>
            enrichmentDataRequestApiMapper(data),
          valueFormatter: enrichmentRequestValueFormatter,
          tags: { labelFormatter: enrichmentRequestParser },
          getTabsCount: enrichmentVisibilityTabsCount,
        },
        searchKey: 'search',
        config: { labelKey: 'name', valueKey: 'id' },
        tabs: VISIBILITY_TABS,
        options: buildEnrichmentVisibilityOptions(
          EnrichmentTypeEnum.PERSON,
          'people_enrichment_request_query'
        ),
      },
    ],
  };

  return {
    id: `Enrichment Requests`,
    name: `In & Exclude Enrichments`,
    icon: 'view-in-ar',
    index: 12,
    type: SearchFilterType.ROOT,
    comingSoon: !isEnrichmentEnabled,
    disabled: !isEnrichmentEnabled,
    pinned: true,
    category: [FilterCategory.RECORD_MANAGEMENT],
    layer: 3,
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        metadata: {
          allowParallelItems: true,
          tabs: {
            defaultOpenIndex: searchType === SearchType.Company ? 0 : 1,
          },
        },
        config: { labelKey: 'label', valueKey: 'value' },
        items: [
          ...[
            {
              name: 'Applied Filters',
              type: SearchFilterType.APPLIED_FILTERS,
              index: 2,
              icon: undefined,
              key: 'enrichment_requests_applied_filters',
              field: 'enrichment_requests_applied_filters',
              metadata: {},
            },
          ],
          ...[companiesEnrichmentRequest, peopleEnrichmentRequest],
        ],
      },
    ],
  };
}
